/**
 * `paywall_shown` — the event that measures whether the paywall converts.
 *
 * WHY IT IS ONE FUNCTION NOW. Phase 8 built this event and three routes each grew their own
 * copy of the same eight lines: a `useRef(false)`, a `useEffect`, the same guard, the same
 * property name. Three copies of a metric are three chances for it to drift, and the way a
 * metric fails is the quiet way — nothing throws, no page breaks, the number in the dashboard
 * simply stops moving and everyone reads the flat line as "the paywall is not converting"
 * rather than "the paywall is not being measured". 15-02 measured that directly: deleting the
 * `trackEvent` call from one route failed nothing in a 334-test suite.
 *
 * THE THREE THINGS IT GUARANTEES, and why each one is here:
 *
 *   - it fires when the paywall is SHOWN. That is the whole point of the event; the offer and
 *     the metric that would reveal its absence disappear together (14-02's finding, recorded in
 *     `useLeadSignals.ts`: a `403 premium_required` read as an ordinary empty state hides both).
 *   - it fires EXACTLY ONCE per mount. The ref is not an optimisation — React re-runs effects
 *     on every dependency change, and a token refresh alone would re-fire it. A denominator
 *     that counts one impression several times makes the conversion rate look worse than it is,
 *     which is an argument for changing a paywall that is working.
 *   - it does NOT fire for an entitled user. An impression counted for someone who was never
 *     shown anything inflates the same denominator from the other end.
 *
 * `trigger_type`, not `reason`: `reason` is a forbidden key in the API's PII filter (it is the
 * key the raw pipeline reason would arrive under), so an event carrying it is answered with a
 * 400 and vanishes. It is also the property name the SERVER half of this event already writes
 * (`routes/capture.ts`), so one query answers the whole event instead of coalescing two names.
 * See `docs/ops/20_product_analytics.md`.
 */

import { useEffect, useRef } from "react";
import { trackEvent } from "@/api";

/** The reasons a paywall can be on screen. Server-side `capture.ts` writes the same vocabulary. */
export type PaywallTrigger = "usage_limit" | "premium_required" | "team_required";

/**
 * Reports one paywall impression.
 *
 * `shown` is the same flag the JSX renders the offer from — deliberately, so the event cannot
 * describe a paywall the user was not actually shown.
 */
export function usePaywallShown(shown: boolean, token: string | null, trigger: PaywallTrigger): void {
  const reportedRef = useRef(false);
  useEffect(() => {
    if (reportedRef.current || !shown) return;
    reportedRef.current = true;
    // Best-effort: analytics must never take a page down. The failure mode this accepts is a
    // lost impression, which is strictly better than a blank dashboard.
    trackEvent("paywall_shown", token, { trigger_type: trigger }).catch(() => {});
  }, [shown, token, trigger]);
}
